import { useMaestroStore } from '../store/maestroStore';
import { TransportCommand } from '../transport/TransportCommand';

const RESEND_STOP_MIN_MS = 2_000;
const HOLD_MAX_MS = 30 * 60_000;

type PauseHold = { rendererId: string; trackId: string; armedAt: number; lastStopAt: number };

let hold: PauseHold | null = null;

export const OCTAVE_RADIO_ID = 'octave_radio';

/** Octave Radio is the playing source (a buffering stream still counts as playing). */
export function isOctaveRadioPlaying(s: { playbackState: string; currentTrack?: { id?: unknown } | null }): boolean {
  return (s.playbackState === 'playing' || s.playbackState === 'loading')
    && String(s.currentTrack?.id || '') === OCTAVE_RADIO_ID;
}

export function clearRadioPauseHold(): void {
  hold = null;
}

function sendStop(rendererId: string): Promise<void> {
  return TransportCommand
    .stopRenderer(rendererId, { caller: 'radio_pause', userInitiated: true })
    .catch(() => {});
}

/** User paused a live stream: a live stream cannot resume, so Pause also Stops the renderer. */
export async function pauseLiveRadio(): Promise<void> {
  const store = useMaestroStore.getState();
  const renderer = store.activeConnectorRenderer;
  const trackId = String(store.currentTrack?.id || '');
  store.setPlaybackState('paused');
  if (renderer?.id && trackId) {
    const now = Date.now();
    hold = { rendererId: renderer.id, trackId, armedAt: now, lastStopAt: now };
  }
  await TransportCommand.pause(true).catch(() => {});
  if (renderer?.id) await sendStop(renderer.id);
}

/**
 * True when a renderer PLAYING echo (or reconcile) must be ignored because the user paused this
 * live stream. Re-sends Stop (rate limited) so a renderer that resumed by itself is told again.
 */
export function holdRadioPauseAgainstEcho(rendererId: string): boolean {
  if (!hold) return false;
  const store = useMaestroStore.getState();
  const now = Date.now();
  if (
    now - hold.armedAt > HOLD_MAX_MS
    || String(store.currentTrack?.id || '') !== hold.trackId
    || store.activeConnectorRenderer?.id !== hold.rendererId
  ) {
    hold = null;
    return false;
  }
  if (rendererId !== hold.rendererId) return false;
  if (store.playbackState !== 'paused' && store.playbackState !== 'stopped') store.setPlaybackState('paused');
  if (now - hold.lastStopAt >= RESEND_STOP_MIN_MS) {
    hold.lastStopAt = now;
    void sendStop(rendererId);
  }
  return true;
}
